import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useDispatch } from "react-redux";

import AppFocusLayout from "../components/AppFocusLayout.jsx";
import Button from "../components/Button.jsx";
import Checkbox from "../components/Checkbox.jsx";
import Icon from "../components/Icon.jsx";
import TextField from "../components/TextField.jsx";

import { setLoading } from "../store";
import i18n from "../i18n.js";
import { classNames, renderHtml } from "../helpers.js";
import registrationApi from "../api/registration";
import { sendError } from "../errors.js";
import Alert from "../components/Alert.jsx";

export default function CompanyRegistration(props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();

  useEffect(async () => {
    window.scrollTo(0, 0);
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setFormErrors({});

    try {
      dispatch(setLoading(true));
      await registrationApi.registerCompany(
        name,
        email,
        companyName,
        website,
        marketing
      );
      setSuccess(true);
    } catch (err) {
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        sendError(err);
        setError(i18n.t("errors.unexpected_error"));
      }
    }
    dispatch(setLoading(false));
  }

  return (
    <AppFocusLayout
      unpadded
      midnight
      hideFooter
      closerAction={
        <Button text onClick={() => history.back()}>
          <Icon>close</Icon>
        </Button>
      }
      class="company-registration__layout"
      title={i18n.t("pages.titles.previewRegistration")}
    >
      <div class="company-registration">
        <div class={classNames("company-registration__top")}>
          <div class="row">
            <div class="col s12 m8 l5">
              <div class={classNames(success && "hide-on-med-and-down")}>
                <h1 class="t-h4 t-h4--margin t-h5--mobile t-h5--margin--mobile">
                  {renderHtml(i18n.t("pages.previewRegistration.headline"))}
                </h1>
                <p class="t-body2 color-shades-charcoal">
                  {renderHtml(i18n.t("pages.previewRegistration.description"))}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="company-registration__right">
          <div class="row row--unpadded-on-med-and-down">
            <div class="col s12 l5">
              <img
                src="/images/preview_reg_n.png"
                class="company-registration__illu hide-on-med-and-down"
              />
            </div>
            <div class="col s12 m8 l6 push-l1 company-registration__box-container">
              {success ? (
                <div class="company-registration__box">
                  <Icon class="mb-48 hide-on-med-and-down">hint_welcome</Icon>
                  <h2 class="t-h5 t-h5--margin">
                    {i18n.t("pages.previewRegistration.success.headline")}
                  </h2>
                  <p class="t-body2 mb-32 color-shades-charcoal">
                    {i18n.t("pages.previewRegistration.success.description")}
                  </p>
                  <p class="t-body4 color-shades-charcoal">
                    {renderHtml(
                      i18n.t("pages.previewRegistration.success.note")
                    )}
                  </p>
                  <p class="mt-48">
                    <Button large secondary to="/business">
                      {i18n.t("actions.ok")}
                    </Button>
                  </p>
                </div>
              ) : (
                <div class="company-registration__box">
                  <h2 class="t-subtitle1 t-subtitle1--margin hide-on-med-and-down">
                    {i18n.t("pages.previewRegistration.formHeadline")}
                  </h2>
                  {error && <Alert error>{error}</Alert>}

                  <TextField
                    name="name"
                    label={i18n.t("pages.previewRegistration.labels.name")}
                    error={formErrors.name}
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                  <TextField
                    name="email"
                    label={i18n.t("pages.previewRegistration.labels.email")}
                    error={formErrors.email}
                    type="text"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <TextField
                    name="companyName"
                    label={i18n.t(
                      "pages.previewRegistration.labels.companyName"
                    )}
                    error={formErrors.companyName}
                    type="text"
                    value={companyName}
                    onChange={(event) => setCompanyName(event.target.value)}
                  />
                  <TextField
                    name="website"
                    label={i18n.t("pages.previewRegistration.labels.website")}
                    error={formErrors.website}
                    type="text"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                  />

                  <p class="mt-32 t-body4 color-shades-charcoal">
                    {renderHtml(
                      i18n.t("pages.previewRegistration.labels.terms")
                    )}
                  </p>

                  <Checkbox
                    multilinable
                    id="question-checkbox"
                    name="marketing"
                    class="mt-16 mb-32 pl-0 color-shades-charcoal"
                    onChange={(event) => setMarketing(event.target.checked)}
                    checked={marketing}
                  >
                    {renderHtml(
                      i18n.t("pages.previewRegistration.labels.marketing")
                    )}
                  </Checkbox>
                  <p class="mt-48 mb-64">
                    <Button large onClick={handleSubmit}>
                      {i18n.t("pages.previewRegistration.submit")}
                    </Button>
                  </p>
                </div>
              )}
              <div class="company-registration__footer">
                <span class="company-registration__footer__section">
                  {i18n.t("footer.copyright")}
                </span>
                <a
                  href={`mailto:${i18n.t("feedback_mail")}`}
                  class="company-registration__footer__section subtle"
                  data-tk-click="footer.link"
                  data-tk-params='{"name":"feedback"}'
                >
                  {i18n.t("footer.feedback")}
                </a>
                <a
                  href="/imprint"
                  class="company-registration__footer__section subtle"
                >
                  {i18n.t("footer.imprint")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppFocusLayout>
  );
}
